/**
 * Tools list page — `GET /tools`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference):
 * `ShellHeader` + `CordioAbpPagedDataTable` + one `DeleteDialog`, with the grid owning paging,
 * search and filtering. The page's `filters` / `totalCount` state and the `useEffect` that refetched
 * on every filter change are gone; `listVersion` is the single piece of refetch state left.
 *
 * ─── Column plan ────────────────────────────────────────────────────────────────────────────────
 * field            │ header   │ width │ cell type        │ filter
 * ─────────────────┼──────────┼───────┼──────────────────┼─────────────────────────────────────────
 * displayName      │ Name     │  22%  │ text (clickable) │ toolbar search (`searchTerm`)
 * name             │ Key      │  16%  │ text             │ —
 * category         │ Category │  16%  │ text             │ popover select → `category` (lazy)
 * riskLevel        │ Risk     │  14%  │ custom Chip      │ —
 * requiresApproval │ Approval │  16%  │ custom Chip      │ —
 * isActive         │ Status   │  16%  │ custom Chip      │ status tabs → synthetic `status`
 *
 * Dropped from the old 7-column table: Type (`isDefault`) — it stays reachable as a filter, which
 * is how people actually use it — and "Runs on" (`executionLocation`), a wiring detail that belongs
 * on the tool detail page rather than in a scanning column. Risk and Approval are kept over both
 * because they are the pair a reviewer scans a tool list for. The MUI `ToolCategoryIcon` glyph that
 * prefixed the category cell is gone with the rest of the MUI table.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * No column is `sortable`: `toolService.getTools` builds its query string from an explicit
 * allow-list (`pageNumber`, `pageSize`, `category`, `isDefault`, `isActive`, `searchTerm`) and has
 * nowhere to put `sortBy` / `sortDescending`, so a sort header would silently do nothing.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type ChipColor,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { Plus, Trash2 } from 'lucide-react';

import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { toolService } from '../services/toolService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { ToolRiskLevel } from '../types/tool';
import { ROUTES } from '../utils/routes';

import type { ToolDto } from '../types/tool';

import './ToolsListPage.scss';

/**
 * Quick-filter tabs over the tool's `isActive` dimension.
 *
 * The All tab declares the literal `'all'` because the grid builds each tab's test id as
 * `cordio-datatable-status-tab-<value>`; an empty value emits `cordio-datatable-status-tab-` and
 * GRID-13's `…-status-tab-all` selector matches nothing.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/**
 * Synthetic toolbar key the status tabs write to. Never sent to the API as-is — `'all'` is a UI
 * concept the endpoint has no value for, so {@link toToolQuery} maps it to `isActive` and drops it
 * entirely for All.
 */
const STATUS_TAB_PARAM_KEY = 'status';

/** How many tools to sample when building the category filter's options. */
const CATEGORY_SAMPLE_SIZE = 200;

/**
 * Turns the grid's query into the one `toolService.getTools` accepts: the synthetic `status` tab
 * value becomes `isActive`, and the popover's `isDefault` select — handed over as the string
 * `'true'` / `'false'` — becomes a real boolean.
 */
function toToolQuery(query: Record<string, unknown>): Record<string, unknown> {
  const { [STATUS_TAB_PARAM_KEY]: status, isDefault, ...rest } = query;

  const mapped: Record<string, unknown> = { ...rest };

  if (status === 'true' || status === 'false') {
    mapped.isActive = status === 'true';
  }
  if (isDefault === 'true' || isDefault === 'false') {
    mapped.isDefault = isDefault === 'true';
  }

  return mapped;
}

/** The risk vocabulary the API's enum values stand for. */
const RISK_LABELS: Record<number, string> = {
  [ToolRiskLevel.Low]: 'Low',
  [ToolRiskLevel.Medium]: 'Medium',
  [ToolRiskLevel.High]: 'High',
  [ToolRiskLevel.Critical]: 'Critical',
};

const RISK_COLORS: Record<number, ChipColor> = {
  [ToolRiskLevel.Low]: 'green',
  [ToolRiskLevel.Medium]: 'amber',
  [ToolRiskLevel.High]: 'red',
  [ToolRiskLevel.Critical]: 'red',
};

export const ToolsListPage = () => {
  const navigate = useNavigate();

  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const { showNotification } = useNotificationStore();

  /** Bumped after a mutation to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<ToolDto>();

  const tenantId = currentTenant?.id;
  const projectId = currentProject?.id;

  const dataSource = useMemo(
    () => createCortexPagedDataSource<ToolDto>((query) => toolService.getTools(toToolQuery(query))),
    // `tenantId` / `projectId` scope the request through the API client's headers rather than the
    // query string, so they still have to invalidate the list. `listVersion` is the deliberate
    // refetch lever: changing the identity of `dataSource` is the documented way to reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, projectId, listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<ToolDto>>>(
    () => [
      { field: 'displayName', header: 'Name', type: 'text', width: '22%', clickable: true },
      // The invocation key an agent's function call actually carries — worth a column of its own,
      // because it is what appears in execution logs and prompts.
      { field: 'name', header: 'Key', type: 'text', width: '16%' },
      { field: 'category', header: 'Category', type: 'text', width: '16%' },
      {
        field: 'riskLevel',
        header: 'Risk',
        type: 'custom',
        width: '14%',
        customCell: (row) => (
          <Chip label={RISK_LABELS[row.riskLevel] ?? 'Unknown'} color={RISK_COLORS[row.riskLevel] ?? 'default'} />
        ),
      },
      {
        field: 'requiresApproval',
        header: 'Approval',
        type: 'custom',
        width: '16%',
        customCell: (row) => (
          <Chip
            label={row.requiresApproval ? 'Required' : 'Not required'}
            color={row.requiresApproval ? 'amber' : 'default'}
          />
        ),
      },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '16%',
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search tools by name' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Tool status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [
        {
          key: 'category',
          label: 'Category',
          type: 'select',
          // The API has no "list categories" endpoint, so the options are derived from a sample of
          // the catalog. Lazy: this runs the first time the popover opens, never on page load.
          loadOptions: async () => {
            const page = await toolService.getTools({ pageNumber: 1, pageSize: CATEGORY_SAMPLE_SIZE });
            const categories = [...new Set(page.items.map((tool) => tool.category).filter(Boolean))].sort();
            return categories.map((category) => ({ label: category, value: category }));
          },
        },
        {
          key: 'isDefault',
          label: 'Type',
          type: 'select',
          options: [
            { label: 'Default', value: 'true' },
            { label: 'Custom', value: 'false' },
          ],
        },
      ],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<ToolDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View tool',
        onClick: (row) => navigate(ROUTES.TOOL_DETAIL.replace(':id', row.id)),
      },
      {
        icon: 'square-pen',
        tooltip: 'Edit tool',
        onClick: (row) => navigate(ROUTES.TOOL_EDIT.replace(':id', row.id)),
      },
      {
        icon: 'play',
        // The runner lives on the detail page; this is the same destination the old Execute icon
        // used, kept as its own action because it is a distinct intent from "read the definition".
        tooltip: 'Execute tool',
        onClick: (row) => navigate(ROUTES.TOOL_DETAIL.replace(':id', row.id)),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete tool',
        className: 'tools-list__action--destructive',
        onClick: (row) => openDelete(row),
      },
    ],
    [navigate, openDelete],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingItem) return;

    await toolService.deleteTool(pendingItem.id);
    showNotification('Tool deleted successfully', 'success');
    setListVersion((v) => v + 1);
  }, [pendingItem, showNotification]);

  return (
    <div className="tools-list" data-testid="tool-list-page">
      <ShellHeader
        title="Tools"
        description="The catalog agents call into — each tool's risk level, whether it needs approval, and whether it is live."
        data-testid="tool-list-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            data-testid="add-tool-btn"
            onClick={() => navigate(ROUTES.TOOL_NEW)}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Create Tool
          </Button>
        }
      />

      <div data-testid="tool-list-grid">
        <CordioAbpPagedDataTable<ToolDto>
          className="tools-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(ROUTES.TOOL_DETAIL.replace(':id', row.id))}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title="Delete tool?"
        entityLabel={pendingItem?.displayName}
        dialogIcon={<Trash2 className="delete-dialog__icon" />}
        description="Any agent that attached this tool loses the capability, and its execution history loses the definition behind it. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        busyLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onCancel={closeDelete}
      />
    </div>
  );
};
